/**
 * Bundle widget v2 — "Complete your bundle" / "Buy it with" accessory picker
 * Companion JS for bundle-widget.liquid (v2) + bundle-widget.css (v2).
 *
 * DOES NOT TOUCH: cacheMainButtonRefs(), handleActionClick(), addBundle(),
 * buildBundleItems(), or anything calling ProductForm.submitItems(). These
 * are the live Add to Cart / Buy Now pathway for the whole site and are
 * carried over unchanged from the previous working version.
 */
(function () {
  const STAR_COLOR = '#fbbf24';
  const STAR_EMPTY = '#e5e7eb';

  class BundleWidget extends HTMLElement {
    constructor() {
      super();
      this.discountPercent = parseFloat(this.dataset.discountPercent || '20');
      this.hasDiscount = this.dataset.hasDiscount === 'true';
      this.accessories = {};
      this._carousels = new Map();
      this._onActionClick = this.handleActionClick.bind(this);
      this._submitting = false;
    }

    connectedCallback() {
      this.summaryLabelTemplate = this.dataset.summaryLabel || '';
      this.saveTemplate = this.dataset.saveText || '';
      this.checkoutMicrocopy = this.dataset.checkoutMicrocopy || '';
      this.variantErrorText = this.dataset.variantErrorText || '';
      this.genericErrorText = this.dataset.genericErrorText || '';
      this.notifyMeText = this.dataset.notifyMeText || '';
      this.expandLabel = 'Show accessory details';
      this.collapseLabel = 'Hide accessory details';

      this.parseAccessoryData();
      this.computeRatings();
      this.bindEvents();
      this.cacheMainButtonRefs();

      document.addEventListener('click', this._onActionClick, true);

      this.recalculate();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onActionClick, true);
    }

    /* ------------------------------------------------------------------ *
     * Add to cart plumbing — UNCHANGED from the previous version.
     * ------------------------------------------------------------------ */

    cacheMainButtonRefs() {
      this.mainFormId = this.dataset.mainFormId || null;
      this.stickyFormId = this.dataset.stickyFormId || null;

      const mainForm = this.mainFormId ? document.getElementById(this.mainFormId) : null;
      const stickyForm = this.stickyFormId ? document.getElementById(this.stickyFormId) : null;

      this.mainForm = mainForm;
      this.stickyForm = stickyForm;
      this.mainAddBtn = mainForm ? mainForm.querySelector('[name="add"]') : null;
      this.stickyAddBtn = stickyForm ? stickyForm.querySelector('[name="add"]') : null;
      this.buyNowBtn = document.getElementById('buy-now');

      [this.mainAddBtn, this.stickyAddBtn].forEach((btn) => {
        if (btn && !btn.dataset.addToCartText) {
          btn.dataset.addToCartText = btn.textContent.trim();
        }
      });
      if (this.buyNowBtn) {
        this.buyNowBaseText = this.buyNowBtn.textContent.trim();
      }
    }

    handleActionClick(event) {
      const clickedBtn = event.target.closest('button');
      if (!clickedBtn) return;
      if (clickedBtn !== this.mainAddBtn && clickedBtn !== this.stickyAddBtn && clickedBtn !== this.buyNowBtn) return;
      if (this.getCheckedAccessoryCount() === 0) return;

      if (this._submitting) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.addBundle({ redirectToCheckout: clickedBtn === this.buyNowBtn, triggerBtn: clickedBtn });
    }

    async addBundle({ redirectToCheckout, triggerBtn }) {
      const errorEl = this.querySelector('[data-bundle-error]');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      const items = this.buildBundleItems();
      if (!items) {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = this.variantErrorText;
        }
        return;
      }

      this._submitting = true;

      const allButtons = [this.mainAddBtn, this.stickyAddBtn, this.buyNowBtn].filter(Boolean);
      allButtons.forEach((b) => {
        b.setAttribute('aria-disabled', 'true');
        if (b === triggerBtn) b.classList.add('is-loading');
      });

      const ProductFormClass = customElements.get('product-form');
      let succeeded = false;

      try {
        const data = await ProductFormClass.submitItems(items);
        succeeded = true;

        if (redirectToCheckout) {
          window.location.href = '/checkout';
          return;
        }

        ProductFormClass.updateCartIcon(data);

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        } else if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        }
      } catch (err) {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = (err && (err.cartError || err.message)) || this.genericErrorText;
        }
        document.dispatchEvent(
          new CustomEvent('on:cart:error', { bubbles: true, detail: { error: errorEl ? errorEl.textContent : null } })
        );
      } finally {
        this._submitting = false;
        allButtons.forEach((b) => {
          if (b === triggerBtn && succeeded) b.classList.add('is-success');
          b.removeAttribute('aria-disabled');
          setTimeout(() => {
            b.classList.remove('is-loading');
            b.classList.remove('is-success');
          }, 1400);
        });
        this.updateButtonLabels(this.getCheckedAccessoryCount());
      }
    }

    getMainVariantId() {
      const input = this.mainForm ? this.mainForm.querySelector('[name="id"]') : null;
      return input ? input.value : null;
    }

    getMainQuantity() {
      const qtyInput = this.mainForm ? this.mainForm.querySelector('[name="quantity"]') : null;
      const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      return qty > 0 ? qty : 1;
    }

    buildBundleItems() {
      const mainVariantId = this.getMainVariantId();
      if (!mainVariantId) return null;

      const items = [{ id: mainVariantId, quantity: this.getMainQuantity() }];
      Object.values(this.accessories).forEach((accessory) => {
        if (accessory.checked && accessory.currentVariant) {
          items.push({ id: accessory.currentVariant.id, quantity: 1 });
        }
      });
      return items;
    }

    /* ------------------------------------------------------------------ *
     * Accessory data / pricing
     * ------------------------------------------------------------------ */

    parseAccessoryData() {
      const cards = this.querySelectorAll('[data-accessory-card]');
      cards.forEach((card) => {
        const id = card.dataset.accessoryId;
        const dataScript = this.querySelector(`script[data-accessory-variants="${id}"]`);
        if (!dataScript) return;

        let data;
        try {
          data = JSON.parse(dataScript.textContent);
        } catch (e) {
          console.error('Bundle widget: could not parse variant data for', id, e);
          return;
        }

        let firstAvailable = null;
        const orderedValues = data.firstOptionValues || [];
        for (const val of orderedValues) {
          const match = data.variants.find((v) => v.available && v.options[0] === val);
          if (match) {
            firstAvailable = match;
            break;
          }
        }
        if (!firstAvailable) {
          firstAvailable = data.variants.find((v) => v.available) || data.variants[0];
        }

        this.accessories[id] = {
          available: data.available,
          variants: data.variants,
          selectedOptions: firstAvailable ? firstAvailable.options.slice() : [],
          checked: false,
          currentVariant: firstAvailable || null,
        };

        this.updatePriceDisplay(id);
      });
    }

    bindEvents() {
      this.querySelectorAll('[data-accessory-checkbox]').forEach((cb) => {
        cb.addEventListener('change', (e) => {
          const id = e.target.dataset.accessoryId;
          if (this.accessories[id]) {
            this.accessories[id].checked = e.target.checked;
            this.recalculate();
          }
        });
      });

this.querySelectorAll('[data-accessory-select]').forEach((select) => {
      // 1. Set the initial precise width on load
      select.style.width = `calc(${select.options[select.selectedIndex].text.length}ch + 36px)`;

      select.addEventListener('change', (e) => {
        const id = e.target.dataset.accessoryId;
        this.selectOption(id, 0, e.target.value);
        
        // 2. Dynamically update the width whenever the user changes the size
        e.target.style.width = `calc(${e.target.options[e.target.selectedIndex].text.length}ch + 36px)`;
      });
    });

      this.querySelectorAll('[data-accessory-expand-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.accessoryId;
          const panel = this.querySelector(`[data-accessory-panel][data-accessory-id="${id}"]`);
          if (!panel) return;

          const isOpen = btn.getAttribute('aria-expanded') === 'true';
          if (isOpen) {
            panel.classList.remove('is-open');
            btn.setAttribute('aria-expanded', 'false');
            btn.setAttribute('aria-label', this.expandLabel);
            window.setTimeout(() => {
              if (!panel.classList.contains('is-open')) panel.hidden = true;
            }, 300);
          } else {
            panel.hidden = false;
            // eslint-disable-next-line no-unused-expressions
            panel.offsetHeight;
            panel.classList.add('is-open');
            btn.setAttribute('aria-expanded', 'true');
            btn.setAttribute('aria-label', this.collapseLabel);

            const carousel = panel.querySelector('[data-accessory-carousel]');
            if (carousel) {
              this.initCarousel(carousel);
            }
          }
        });
      });

      this.querySelectorAll('[data-notify-btn]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const id = btn.dataset.accessoryId;
          this.dispatchEvent(
            new CustomEvent('bundle:notify-me-click', {
              bubbles: true,
              detail: { accessoryId: id },
            })
          );
        });
      });
    }

    /**
     * Initialise (or re-render) a carousel. Called when the expand panel
     * opens so images inside hidden panels get correct dimensions and
     * lazy-loaded imgs are already eager from Liquid.
     */
    initCarousel(carousel) {
      const id = carousel.dataset.accessoryId || carousel.getAttribute('data-accessory-id');
      const track = carousel.querySelector('[data-carousel-track]');
      const slides = carousel.querySelectorAll('[data-carousel-slide]');
      const prevBtn = carousel.querySelector('[data-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-carousel-next]');
      const dots = carousel.querySelectorAll('[data-carousel-dot]');
      if (!track || slides.length === 0) return;

      let state = this._carousels.get(id);
      if (!state) {
        state = { index: 0 };
        this._carousels.set(id, state);

        const render = () => {
          track.style.transform = `translateX(-${state.index * 100}%)`;
          dots.forEach((dot, i) => dot.classList.toggle('is-active', i === state.index));
        };

        const goTo = (i) => {
          state.index = (i + slides.length) % slides.length;
          render();
        };

        if (prevBtn) prevBtn.addEventListener('click', () => goTo(state.index - 1));
        if (nextBtn) nextBtn.addEventListener('click', () => goTo(state.index + 1));
        dots.forEach((dot) => {
          dot.addEventListener('click', () => goTo(parseInt(dot.dataset.index, 10) || 0));
        });

        let touchStartX = null;
        track.addEventListener(
          'touchstart',
          (e) => {
            touchStartX = e.touches[0].clientX;
          },
          { passive: true }
        );
        track.addEventListener(
          'touchend',
          (e) => {
            if (touchStartX === null) return;
            const delta = e.changedTouches[0].clientX - touchStartX;
            if (Math.abs(delta) > 40) goTo(delta < 0 ? state.index + 1 : state.index - 1);
            touchStartX = null;
          },
          { passive: true }
        );

        state.render = render;
        state.goTo = goTo;
      }

      state.index = Math.min(state.index, slides.length - 1);
      state.render();
    }

    computeRatings() {
      this.querySelectorAll('[data-accessory-card]').forEach((card) => {
        const id = card.dataset.accessoryId;

        const badgeSlot = this.querySelector(`[data-jdgm-badge-slot][data-accessory-id="${id}"]`);
        const badge = badgeSlot ? badgeSlot.querySelector('.jdgm-prev-badge') : null;

        let internalAvg = 0;
        let internalCount = 0;
        if (badge) {
          internalAvg = parseFloat(badge.getAttribute('data-average-rating')) || 0;
          internalCount = parseInt(badge.getAttribute('data-number-of-reviews'), 10) || 0;
        }

        const ratingEls = this.querySelectorAll(`[data-accessory-rating][data-accessory-id="${id}"]`);
        if (ratingEls.length === 0) return;

        const externalAvg = parseFloat(ratingEls[0].dataset.externalAvg) || 0;
        const externalCount = parseInt(ratingEls[0].dataset.externalCount, 10) || 0;

        if (internalCount === 0 && externalCount === 0) return;

        let totalWeighted = 0;
        let totalCount = 0;
        if (internalCount > 0) {
          totalWeighted += internalAvg * internalCount;
          totalCount += internalCount;
        }
        if (externalCount > 0) {
          totalWeighted += externalAvg * externalCount;
          totalCount += externalCount;
        }

        const combinedAvg = totalWeighted / totalCount;
        const roundedStars = Math.round(combinedAvg);

        let starsHTML = '';
        for (let i = 1; i <= 5; i++) {
          starsHTML += `<svg viewBox="0 0 24 24" fill="${i <= roundedStars ? STAR_COLOR : STAR_EMPTY}" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
        }

        ratingEls.forEach((el) => {
          const starsEl = el.querySelector('[data-rating-stars]');
          const countEl = el.querySelector('[data-rating-count]');
          if (starsEl) starsEl.innerHTML = starsHTML;
          if (countEl) countEl.textContent = `${combinedAvg.toFixed(1)} (${totalCount})`;
          el.hidden = false;
        });
      });
    }

    selectOption(id, optionIndex, value) {
      const accessory = this.accessories[id];
      if (!accessory) return;

      accessory.selectedOptions[optionIndex] = value;

      const match = accessory.variants.find((v) =>
        v.options.every(
          (optVal, i) => accessory.selectedOptions[i] == null || optVal === accessory.selectedOptions[i]
        )
      );
      if (match) accessory.currentVariant = match;

      this.updatePriceDisplay(id);
      this.recalculate();
    }

    updatePriceDisplay(id) {
      const accessory = this.accessories[id];
      if (!accessory || !accessory.currentVariant) return;

      const priceWrap = this.querySelector(`[data-accessory-price][data-accessory-id="${id}"]`);
      if (!priceWrap) return;

      const variant = accessory.currentVariant;
      const priceInner = priceWrap.querySelector('.bundle-widget__price-inner');
      const currentEl = priceWrap.querySelector('.price__current .js-value');
      const wasWrap = priceWrap.querySelector('.price__was');
      const wasEl = wasWrap ? wasWrap.querySelector('.js-value') : null;

      if (currentEl) {
        currentEl.textContent = this.formatMoney(variant.price);
      }

      const onSale = variant.compareAtPrice && variant.compareAtPrice > variant.price;
      if (wasWrap) {
        if (onSale && wasEl) {
          wasEl.textContent = this.formatMoney(variant.compareAtPrice);
          wasWrap.hidden = false;
          if (priceInner) priceInner.classList.add('price--on-sale');
        } else {
          wasWrap.hidden = true;
          if (priceInner) priceInner.classList.remove('price--on-sale');
        }
      }

      if (!this.hasDiscount) return;

      const discountedEl = priceWrap.querySelector('[data-accessory-discounted-price]');
      if (!discountedEl) return;

      const discounted = Math.round(variant.price * (1 - this.discountPercent / 100));
      discountedEl.textContent = this.formatMoney(discounted);
      discountedEl.hidden = false;
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        try {
          return Shopify.formatMoney(cents, window.theme_money_format || '${{amount}}');
        } catch (e) {
          /* fall through */
        }
      }
      return '$' + (cents / 100).toFixed(2);
    }

    getCheckedAccessoryCount() {
      return Object.values(this.accessories).filter((a) => a.checked && a.currentVariant).length;
    }

    recalculate() {
      const summaryEl = this.querySelector('[data-bundle-summary]');

      if (!this.hasDiscount) {
        this.updateButtonLabels(this.getCheckedAccessoryCount());
        return;
      }

      let totalCents = 0;
      let saveCents = 0;
      let count = 0;

      Object.values(this.accessories).forEach((accessory) => {
        if (accessory.checked && accessory.currentVariant) {
          const original = accessory.currentVariant.price;
          const discounted = Math.round(original * (1 - this.discountPercent / 100));
          totalCents += discounted;
          saveCents += original - discounted;
          count += 1;
        }
      });

      const countLabel = this.querySelector('[data-bundle-count-label]');
      const totalEl = this.querySelector('[data-bundle-total]');
      const saveEl = this.querySelector('[data-bundle-save]');
      const microcopyEl = this.querySelector('[data-bundle-microcopy]');

      if (summaryEl) summaryEl.hidden = count === 0;

      if (countLabel && this.summaryLabelTemplate) {
        countLabel.textContent = this.summaryLabelTemplate.replace('[count]', count);
      }
      if (totalEl) totalEl.textContent = this.formatMoney(totalCents);
      if (saveEl && this.saveTemplate) {
        saveEl.textContent = this.saveTemplate.replace('[amount]', this.formatMoney(saveCents));
      }
      if (microcopyEl) microcopyEl.textContent = this.checkoutMicrocopy;

      this.updateButtonLabels(count);
    }

    updateButtonLabels(count) {
      [this.mainAddBtn, this.stickyAddBtn].forEach((btn) => {
        if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
        const base = btn.dataset.addToCartText;
        if (!base) return;
        btn.textContent = base;
      });

      if (
        this.buyNowBtn &&
        !this.buyNowBtn.disabled &&
        this.buyNowBtn.getAttribute('aria-disabled') !== 'true' &&
        this.buyNowBaseText
      ) {
        this.buyNowBtn.textContent = this.buyNowBaseText;
      }
    }
  }

  if (!customElements.get('bundle-widget')) {
    customElements.define('bundle-widget', BundleWidget);
  }
})();
